import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/auth';
import { apiClient } from '../../api/axios';
import { AlertTriangle, ArrowRight } from 'lucide-react';

// Routes where the gate shouldn't appear (mirrors ProfileCompletionModal).
const EXCLUDED_ROUTES = ['/profile', '/admin/profile', '/author/profile', '/login', '/signup', '/verify-account', '/reset-password', '/change-password'];

/**
 * Blocks reviewers who have finished their profile but haven't added bank details.
 * Sits after the profile gate (profile first, then bank), so the two never stack.
 */
const BankDetailsCompletionModal: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated } = useAuth();

    const isReviewer = user?.role === 'accessor';
    const eligible = isAuthenticated && isReviewer && user?.profileCompleted === true;

    const [hasBank, setHasBank] = useState<boolean | null>(null);

    const check = useCallback(async () => {
        if (!eligible) return;
        try {
            const res = await apiClient.get('/BankAccountDetails/GetMyBankAccountDetail');
            const data = res.data?.data;
            const hasAny = !!(data && (data.accountName || data.accountNumber || data.bankName));
            setHasBank(!!res.data?.isSuccess && hasAny);
        } catch {
            // On a transient error, don't lock the user out.
            setHasBank(true);
        }
    }, [eligible]);

    // Re-check on navigation (until complete) and when the bank tab reports a save.
    useEffect(() => {
        if (eligible && hasBank !== true) check();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligible, location.pathname]);

    useEffect(() => {
        const onUpdated = () => check();
        window.addEventListener('bank-details-updated', onUpdated);
        return () => window.removeEventListener('bank-details-updated', onUpdated);
    }, [check]);

    const isExcluded = EXCLUDED_ROUTES.some(route => location.pathname === route || location.pathname.startsWith(route));

    if (!eligible || hasBank !== false || isExcluded) return null;

    const goToBankDetails = () => navigate('/admin/profile', { state: { fromModal: true, tab: 'bank' } });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                <div className="bg-white p-6 text-center border-b border-gray-100">
                    <div className="mx-auto bg-yellow-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 border border-yellow-100">
                        <AlertTriangle className="w-10 h-10 text-yellow-500" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Add Your Bank Details</h2>
                    <p className="text-gray-500 mt-2 text-sm font-medium">Action Required to Continue</p>
                </div>

                <div className="p-8">
                    <p className="text-gray-600 text-center mb-8 leading-relaxed">
                        Welcome, <span className="font-semibold text-gray-900">{user?.name}</span>.
                        Before you continue, please add the bank account where your reviewer payments should be made.
                    </p>

                    <button
                        onClick={goToBankDetails}
                        className="w-full bg-[#088A61] text-white py-3.5 rounded-lg font-semibold
                     hover:bg-[#067451] transition-colors shadow-lg shadow-green-900/10
                     flex items-center justify-center gap-2 group"
                    >
                        Go to Bank Details
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BankDetailsCompletionModal;
